import * as React from 'react';
import { BookingEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';
import { CargoCharacteristicDto, mapCargoCharacteristicEntityToDto, withLinkedCharacteristics } from 'Modules/Cargo';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { getMeasurementLabelFromId, getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { roundToDecimals } from 'Util/_HumanWritten/NumberUtils';
import { convertWeight, parseWeightUnit, roundWeightForUnit } from 'Util/Weight';
import { Flex } from 'Views/Components/Flex/Flex';
import CargoCharacteristicTags from 'Views/Components/_HumanWritten/Tag/CargoCharacteristicTags';
import { CheckInBookingOverviewDto } from '../../CheckInEntities/CheckInBookingOverviewDto';
import { ViewOption } from '../../CheckInSortAndFilterHelpers/CheckInSortStore';
import { ICheckInStore } from '../../context/CheckInContext';

function resolveWeightInDisplayUnit(
	exactWeight: number | null | undefined,
	exactWeightUnit: string | null | undefined,
	selectedWeightId: string | null | undefined,
): number {
	const displayUnit = appConfigStore.weightDisplayUnit;
	if (exactWeight != null) {
		const storedUnit = parseWeightUnit(exactWeightUnit, displayUnit);
		return roundWeightForUnit(convertWeight(Number(exactWeight), storedUnit, displayUnit), displayUnit);
	}
	const bandValueKg = getMeasurementValueFromId(selectedWeightId ?? undefined);
	return roundWeightForUnit(convertWeight(bandValueKg ?? 0, 'kg', displayUnit), displayUnit);
}

export function transformVehicleMakeModel(booking: BookingEntity | CheckInBookingOverviewDto) {
	if (!booking.bookedSummary) {
		throw new Error('Booked summary missing');
	}

	const { bookedSummary } = booking;
	const isVehicleBooking = isNotNullOrUndefined(bookedSummary.cargoInfo);

	let vehicleMakeModel = null;

	if (isVehicleBooking) {
		const make = bookedSummary?.cargoInfo?.cargoType.cargoMake;
		const model = bookedSummary?.cargoInfo?.cargoType.cargoModel;
		vehicleMakeModel = `${make} ${model}`;
	}

	if (vehicleMakeModel === '' || vehicleMakeModel === null) {
		return '-';
	}

	return vehicleMakeModel;
}

export function transformVehicleHeader(booking: CheckInBookingOverviewDto, checkInStore: ICheckInStore) {
	const makeAndModel = transformVehicleMakeModel(booking);

	if (!booking.bookedSummary) {
		throw new Error('Booked summary missing');
	}

	const {
		bookedSummary: { cargoInfo },
	} = booking;

	if (cargoInfo) {
		const vehicleMap = checkInStore.getVehicleTypeMap();
		const cargoType = vehicleMap.get(cargoInfo.cargoTypeId);
		if (cargoType) {
			const cargoCharacteristicMap: Record<string, CargoCharacteristicDto> = {};
			checkInStore.cargoCharacteristics.forEach(c => {
				cargoCharacteristicMap[c.id] = mapCargoCharacteristicEntityToDto(c);
			});

			// A characteristic flagged "Link trailer/vehicle" carries between the vehicle and its
			// trailer, so merge the trailer's linked characteristics into the vehicle column (only
			// when a trailer exists, so a linked vehicle characteristic doesn't show a phantom tag).
			const vehicleEffectiveCharacteristics = withLinkedCharacteristics(
				booking.bookedSummary.effectiveCharacteristics,
				isNotNullOrUndefined(booking.bookedSummary.towOnInfo)
					? booking.bookedSummary.trailerEffectiveCharacteristics
					: [],
				cargoCharacteristicMap,
			);

			return (
				<Flex gap="xxs" align="center" flexWrap="wrap">
					<span>{makeAndModel}</span>
					<CargoCharacteristicTags
						effectiveCharacteristics={vehicleEffectiveCharacteristics}
						cargoCharacteristicMap={cargoCharacteristicMap}
						respectVisibility
					/>
				</Flex>
			);
		}
	}

	return makeAndModel;
}

export function transformTrailerHeader(booking: CheckInBookingOverviewDto, checkInStore: ICheckInStore) {
	if (!booking.bookedSummary) {
		throw new Error('Booked summary missing');
	}

	const {
		bookedSummary: { towOnInfo, cargoInfo },
	} = booking;

	// No trailer on this booking — nothing to show in the trailer type column.
	if (!towOnInfo) {
		return '-';
	}

	const cargoCharacteristicMap: Record<string, CargoCharacteristicDto> = {};
	checkInStore.cargoCharacteristics.forEach(c => {
		cargoCharacteristicMap[c.id] = mapCargoCharacteristicEntityToDto(c);
	});

	// A characteristic flagged "Link trailer/vehicle" carries between the vehicle and its trailer,
	// so merge the vehicle's linked characteristics into the trailer column (only when a vehicle
	// exists, so a linked trailer characteristic doesn't show a phantom tag).
	const trailerEffectiveCharacteristics = withLinkedCharacteristics(
		booking.bookedSummary.trailerEffectiveCharacteristics,
		isNotNullOrUndefined(cargoInfo) ? booking.bookedSummary.effectiveCharacteristics : [],
		cargoCharacteristicMap,
	);

	return (
		<Flex gap="xxs" align="center" flexWrap="wrap">
			<span>{booking.getDisplayTrailerType()}</span>
			<CargoCharacteristicTags
				effectiveCharacteristics={trailerEffectiveCharacteristics}
				cargoCharacteristicMap={cargoCharacteristicMap}
				respectVisibility
			/>
		</Flex>
	);
}

export function transformVehicleSpace(booking: BookingEntity | CheckInBookingOverviewDto, showWeight: boolean = false) {
	if (!booking || !booking.bookedSummary) return null;
	const { cargoInfo, towOnInfo } = booking.bookedSummary;
	if (!cargoInfo) {
		return null;
	}

	if (showWeight) {
		return resolveWeightInDisplayUnit(cargoInfo.exactWeight, cargoInfo.exactWeightUnit, cargoInfo.selectedWeightId);
	}

	// The vehicle uses its own width multiplier; when the vehicle/trailer widths are linked a
	// vehicle with no width of its own falls back to the trailer's width (matches the backend).
	const effectiveVehicleWidthId = appConfigStore.vehicleAndTrailerWidthLinked
		? (cargoInfo.selectedWidthId ?? towOnInfo?.selectedWidthId)
		: cargoInfo.selectedWidthId;
	const multiplier = store.getWidthCapacityMultiplier(effectiveVehicleWidthId ?? '');
	const vehicleLength = getMeasurementValueFromId(cargoInfo.selectedLengthId);

	// prevent floating point number
	return roundToDecimals(multiplier * vehicleLength, 2);
}

export function transformRego(booking: BookingEntity | CheckInBookingOverviewDto) {
	if (!booking || !booking.bookedSummary) {
		return null;
	}
	const { cargoInfo } = booking.bookedSummary;
	if (!cargoInfo) {
		return null;
	}
	return cargoInfo.cargoIdentification.toUpperCase();
}

export interface TripleMetric {
	length: string;
	spaces: number;
	weight: number;
}

export function transformVehicleAllMetrics(booking: BookingEntity | CheckInBookingOverviewDto): TripleMetric | null {
	if (!booking || !booking.bookedSummary) return null;
	const { cargoInfo, towOnInfo } = booking.bookedSummary;
	if (!cargoInfo) return null;

	const effectiveVehicleWidthId = appConfigStore.vehicleAndTrailerWidthLinked
		? (cargoInfo.selectedWidthId ?? towOnInfo?.selectedWidthId)
		: cargoInfo.selectedWidthId;
	const multiplier = store.getWidthCapacityMultiplier(effectiveVehicleWidthId ?? '');
	const vehicleLength = getMeasurementLabelFromId(cargoInfo.selectedLengthId);
	const vehicleSpace = getMeasurementValueFromId(cargoInfo.selectedLengthId);
	const weight = resolveWeightInDisplayUnit(
		cargoInfo.exactWeight,
		cargoInfo.exactWeightUnit,
		cargoInfo.selectedWeightId,
	);

	return {
		length: vehicleLength,
		spaces: roundToDecimals(multiplier * vehicleSpace, 2),
		weight,
	};
}

export function formatMetricPills(metrics: TripleMetric | null, viewOptions: ViewOption[]): React.ReactNode {
	if (!metrics || viewOptions.length === 0) return '-';

	return (
		<span className="metric-pills-row">
			{viewOptions.includes('spaces') && <span className="metric-pill blue">{metrics.spaces}</span>}
			{viewOptions.includes('weight') && <span className="metric-pill red">{metrics.weight}</span>}
			{viewOptions.includes('length') && <span className="metric-pill green">{metrics.length}</span>}
		</span>
	);
}
